import { useCallback, useEffect, useState } from "react";

export const userMediaErrors = {
  CAMERA_API_DO_NOT_SUPPORT: "CAMERA_API_DO_NOT_SUPPORT",
  CAMERA_ACCESS_UNKNOWN_ERROR: "CAMERA_ACCESS_UNKNOWN_ERROR",
  CAMERA_ACCESS_DENIED: "CAMERA_ACCESS_DENIED",
  CAMERA_ACCESS_CAMERA_IN_USE: "CAMERA_ACCESS_CAMERA_IN_USE",
  CAMERA_ACCESS_NO_CAMERA_FOUND: "CAMERA_ACCESS_NO_CAMERA_FOUND",
} as const;

export type UserMediaErrors = (typeof userMediaErrors)[keyof typeof userMediaErrors];

export interface UseUserMediaProps {
  constraints: MediaStreamConstraints;
}
export const useUserMedia = ({ constraints }: UseUserMediaProps) => {
  const [error, setError] = useState<UserMediaErrors | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCameraError = (err: unknown) => {
    if (err instanceof DOMException) {
      switch (err.name) {
        case "NotFoundError":
        case "DevicesNotFoundError":
          setError(userMediaErrors.CAMERA_ACCESS_NO_CAMERA_FOUND);
          break;
        case "NotReadableError":
        case "TrackStartError":
          setError(userMediaErrors.CAMERA_ACCESS_CAMERA_IN_USE);
          break;
        case "NotAllowedError":
        case "PermissionDeniedError":
          setError(userMediaErrors.CAMERA_ACCESS_DENIED);
          break;
        default:
          //   setError(`Cannot access camera: ${err.message}`);
          setError(userMediaErrors.CAMERA_ACCESS_UNKNOWN_ERROR);
      }
    } else {
      setError(userMediaErrors.CAMERA_ACCESS_UNKNOWN_ERROR);
    }
  };

  const initializeCamera = useCallback(async () => {
    setIsLoading(true);
    try {
      // Check for media devices support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError(userMediaErrors.CAMERA_API_DO_NOT_SUPPORT);
        return;
      }

      const ms = await navigator.mediaDevices.getUserMedia(constraints);
      setMediaStream(ms);
    } catch (err) {
      handleCameraError(err);
    } finally {
      setIsLoading(false);
    }
  }, [constraints, setMediaStream, setIsLoading]);

  const closeCamera = useCallback(() => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
    }
  }, [mediaStream]);

  useEffect(
    () => () => {
      closeCamera();
    },
    [closeCamera],
  );

  return { initializeCamera, mediaStream, error, closeCamera, isLoading };
};
